import { ButtonType, ButtonWidget, TabStyles, TextAreaWidget } from '@tableau/widgets';
import React from 'react';

import { ButtonWithSpinner } from '~common/react/components/ButtonWithSpinner';
import { FormattingHints } from '~common/react/components/FormattingHints';
import { characterCounter } from '~src/app/content/CharacterCounter';
import { Messages } from '~src/codegen/Localize';
import { MarkupField } from '~src/spacesPlatform/components/Explorer/widgets/MarkupField';
import { cssClass } from '~src/style';


const MAX_DESCRIPTION_LENGTH = 4000;
const FLOW_EDITABLE_TEXT_AREA = 'flow-editable-text-area';
const MARKUP_TEST_ID = FLOW_EDITABLE_TEXT_AREA + '-markup-field';
export const PREVIEW_BUTTON_TEST_ID = FLOW_EDITABLE_TEXT_AREA + '-preview-button';
export const SAVE_BUTTON_TEST_ID = FLOW_EDITABLE_TEXT_AREA + '-save-button';
export const CANCEL_BUTTON_TEST_ID = FLOW_EDITABLE_TEXT_AREA + '-cancel-button';

export type FlowDescriptionDialogBodyProps = {
  description: string;
  dismissDialog: () => void;
  doAction: (description: string) => void;
  textAreaLabel: string;
}

export function FlowDescriptionField(props: FlowDescriptionDialogBodyProps): JSX.Element {
  const [preview, setPreview] = React.useState(false);
  const [description, setDescription] = React.useState(props.description);
  const togglePreview = () => {
    setPreview(!preview);
  };

  const onTextAreaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setDescription(e.target.value);
  };

  const saveDescription = () => {
    props.doAction(description);
  };

  const actionDisabled = description.length > MAX_DESCRIPTION_LENGTH || description === props.description;

  return (
    (preview) ?
      <>
        <MarkupField testId={MARKUP_TEST_ID} markup={description} />
        <FlowDescriptionFooter
          dismissDialog={() => props.dismissDialog()}
          togglePreviewMode={togglePreview}
          doAction={saveDescription}
          previewMode={preview}
          actionDisabled={actionDisabled}
        />
      </> :
      <>
        <>
          <TextAreaWidget
            label=''
            aria-label={props.textAreaLabel}
            testId={FLOW_EDITABLE_TEXT_AREA}
            message={characterCounter({ value: description, maximum: MAX_DESCRIPTION_LENGTH })}
            rows={16}
            className={textArea}
            style={textAreaStyles}
            onChange={onTextAreaChange}
            value={description}
            valid={description.length <= MAX_DESCRIPTION_LENGTH ? undefined : false}
          />
          <FormattingHints />
        </>
        <FlowDescriptionFooter
          dismissDialog={() => props.dismissDialog()}
          togglePreviewMode={togglePreview}
          doAction={saveDescription}
          previewMode={preview}
          actionDisabled={actionDisabled}
        />
      </>
  );
}

export interface FlowDescriptionFooterProps {
  dismissDialog: () => void;
  doAction: () => void;
  togglePreviewMode: () => void;
  previewMode: boolean;
  actionDisabled: boolean;
}

export const FlowDescriptionFooter = (props: FlowDescriptionFooterProps): JSX.Element => {

  const [busy, setBusy] = React.useState(false);

  const doAction = () => {
    setBusy(true);
    props.doAction();
  };

  return (
    <div style={{ marginTop: TabStyles.Spacing.MediumSpace }}>
      <ButtonWidget
        buttonType={ButtonType.LowEmphasis}
        onClick={() => props.togglePreviewMode()}
        testId={PREVIEW_BUTTON_TEST_ID}
      >{props.previewMode ? Messages.edit() : Messages.preview()}</ButtonWidget>
      <span className={saveAndCancel}>
        <ButtonWithSpinner
          buttonType={ButtonType.Outline}
          clicked={() => props.dismissDialog()}
          testId={CANCEL_BUTTON_TEST_ID}
          title={Messages.cancel()}
          disabled={busy}
        />
        <ButtonWithSpinner
          style={{ marginLeft: TabStyles.Spacing.MediumSpace }}
          buttonType={ButtonType.Primary}
          clicked={doAction}
          testId={SAVE_BUTTON_TEST_ID}
          title={Messages.save()}
          disabled={busy || props.actionDisabled}
          showSpinner={busy}
        />
      </span>
    </div>
  );
};


export const textArea = cssClass(FLOW_EDITABLE_TEXT_AREA, 'textArea', {
  width: '100%',
});

export const saveAndCancel = cssClass(FLOW_EDITABLE_TEXT_AREA, 'save-and-cancel', {
  float: 'right'
});

const textAreaStyles: React.CSSProperties = {
  resize: 'vertical',
  maxHeight: '520px',
  minHeight: '120px'
};
